import moment from "moment";
import type { TFunction } from "react-i18next";
import type {  CellProps, Column } from "react-table";
import styled from "styled-components";
import { ViewTransactionTableCell } from "../../components/tables/ViewTransactionTableCell";
import {
	Status,
	TransactionType,
	statusRenderMappings,
} from "../../lib/constants";
import { formatAmountWithDecimals, formatEntity } from "../../lib/utils";
import type { Transaction } from "../../services/wallet-service";

type TransactionTypeRenderMappings = {
	[key in TransactionType]: { color: string; text: string };
};

const ColoredSpan = styled.span`
  color: ${({ color }) => color};
`;

const StyledStatus = ({
	status: creditStatus,
	translation,
}: {
	status: string;
	translation: TFunction;
}): JSX.Element => {
	const transactionTypeRenderMappings: TransactionTypeRenderMappings = {
		[TransactionType.DEBIT]: { color: "#EC3D08", text: translation("debit") },
		[TransactionType.CREDIT]: { color: "#4AB0A6", text: translation("credit") },
	};

	const { color, text } =
		transactionTypeRenderMappings[
			creditStatus === "CREDIT" ? TransactionType.CREDIT : TransactionType.DEBIT
		];
	return <ColoredSpan {...{ color }}>{text}</ColoredSpan>;
};

const StyledTransactionStatus = ({
	status,
}: {
	status: string;
	translation: TFunction;
}): JSX.Element => {
	const { color, text } =
		statusRenderMappings[
			status === "SUCCESSFUL" ? Status.SUCCESSFUL : status === "PENDING" ? Status.PENDING : Status.PENDING
		];
	return (
		<span style={{ color: `${color}`}} className={`${status.toLowerCase() === "pending" ? "animate-pulse" : ""}`}
		>
			{text}
		</span>
	);
};

const FormattedAmount = ({ amount }: { amount: number }): JSX.Element => (
	<div style={{ textAlign: "right" }}>
		{formatAmountWithDecimals(amount, 2) || "\u2014"}
	</div>
);

export const getTransactionsTableColumnConfig = (
	t: TFunction,
	tokenSymbol,
): Column<Transaction>[] => [
	{
		Header: t("wallet"),
		accessor: "wallet",
		Cell: ({ value }: CellProps<Transaction, string>) => <span>{value}</span>,
	},
	{
		Header: t("contra.wallet"),
		accessor: "contraWallet",
		Cell: ({ value }: CellProps<Transaction, string>) => <span>{value}</span>,
	},
	{
		Header: t("transaction.type"),
		accessor: "type",
	},
	{
		Header: t("entity"),
		accessor: "entity",
		Cell: ({ value }: CellProps<Transaction, string>) => (
			<span>{formatEntity(value) || ""}</span>
		),
	},
	{
		Header: t("transaction.time"),
		accessor: "createdAt",
		Cell: ({ value }: CellProps<Transaction, string>) => (
			<span>{moment(value).format("DD-MM-YYYY, h:mm:ss a")}</span>
		),
	},

	{
		Header: t("type"),
		accessor: "entry",
		Cell: ({ value }: CellProps<Transaction>) => (
			<StyledStatus status={value} translation={t} />
		),
		disableSortBy: true,
	},
	{
		Header: t("transaction.status"),
		accessor: "status",
		Cell: ({ value }: CellProps<Transaction>) => (
			<StyledTransactionStatus status={value} translation={t} />
		),
		disableSortBy: true,
	},
	{
		Header: () => (
			<div
				style={{
					textAlign: "right",
				}}
			>
				{t("amount")} {tokenSymbol ? `(${tokenSymbol})` : ""}
			</div>
		),
		accessor: "amount",
		Cell: (props) => <FormattedAmount amount={props.value} />, // use 2 as default for decimals
	},
	{
		Header: t("view.title"),
		Cell: (props: { row: { original: Transaction } }) => (
			<ViewTransactionTableCell data={props.row.original} />
		),
		disableSortBy: true,
	},
	{
		Header: t("transaction.Id"),
		accessor: "hash",
		Cell: ({ value }: CellProps<Transaction, string>) => <span>{value}</span>,
	},
];
